import { useState } from "react";

import { saveCollection } from "./api";
import { navigate } from "./navigation";
import type { SchoolSubject } from "./types";

type CardDraft = { question: string; answer: string };
type Mode = "memo" | "asso" | "brio" | "typo" | "quizako";

const modeLabels: Record<Mode, string> = {
  memo: "Memo",
  asso: "Asso",
  brio: "Brio",
  typo: "Typo",
  quizako: "Quizako",
};

export function CollectionEditor({ subjects, canOfficial }: { subjects: SchoolSubject[]; canOfficial: boolean }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [subjectId, setSubjectId] = useState(subjects[0]?.id ?? 0);
  const [cards, setCards] = useState<CardDraft[]>([{ question: "", answer: "" }]);
  const [modes, setModes] = useState<Record<Mode, boolean>>({ memo: false, asso: false, brio: false, typo: false, quizako: false });
  const [isPublished, setIsPublished] = useState(true);
  const [isOfficial, setIsOfficial] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateCard = (index: number, field: keyof CardDraft, value: string) => {
    setCards((current) => current.map((card, position) => position === index ? { ...card, [field]: value } : card));
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving || !title.trim() || !subjectId || cards.some((card) => !card.question.trim() || !card.answer.trim())) return;
    setSaving(true);
    setError(null);
    try {
      const result = await saveCollection({
        title: title.trim(),
        description: description.trim(),
        subject_id: subjectId,
        cards: cards.map((card) => ({ question: card.question.trim(), answer: card.answer.trim() })),
        modes,
        is_published: isPublished,
        is_official: canOfficial && isOfficial,
      });
      navigate(result.path);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "La série n’a pas pu être créée.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="collection-page folder-editor-page">
      <button className="back-button" type="button" onClick={() => navigate("/mes-series")}>← Annuler et revenir aux séries</button>
      <div className="collection-heading"><div><p className="eyebrow">Création pédagogique</p><h1>Créer une série</h1><p>Ajoutez directement des cartes recto-verso, sans préparer de fichier d’import.</p></div></div>
      {error && <div className="info-notice" role="alert">{error}</div>}
      <form className="folder-editor-form" onSubmit={(event) => void submit(event)}>
        <div className="folder-fields">
          <label>Titre<span>{title.length}/150</span><input required maxLength={150} value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Ex. Vocabulaire du chapitre 3" /></label>
          <label>Matière<select required value={subjectId || ""} onChange={(event) => setSubjectId(Number(event.target.value))}><option value="" disabled>Choisir une matière</option>{subjects.map((subject) => <option key={subject.id} value={subject.id}>{subject.name}</option>)}</select></label>
          <label className="wide">Description<span>{description.length}/250</span><textarea maxLength={250} rows={3} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Décrivez l’objectif pédagogique." /></label>
          <label className="wide official-checkbox"><input type="checkbox" checked={isPublished} onChange={(event) => setIsPublished(event.target.checked)} /><span><strong>Publier immédiatement</strong><small>Décochez pour conserver la série comme brouillon personnel.</small></span></label>
          {canOfficial && <label className="wide official-checkbox"><input type="checkbox" checked={isOfficial} onChange={(event) => { setIsOfficial(event.target.checked); if (event.target.checked) setIsPublished(true); }} /><span><strong>Ressource officielle</strong><small>Réservé aux référents pédagogiques ; la série sera publiée.</small></span></label>}
        </div>
        <fieldset className="folder-series-picker"><legend>Modes de jeu <span>optionnels</span></legend><div className="folder-series-grid">{(Object.keys(modeLabels) as Mode[]).map((mode) => <label key={mode} className={modes[mode] ? "selected" : ""}><input type="checkbox" checked={modes[mode]} onChange={(event) => setModes({ ...modes, [mode]: event.target.checked })} /><span><strong>{modeLabels[mode]}</strong><small>Disponible dès que la série contient assez de cartes compatibles.</small></span></label>)}</div></fieldset>
        <fieldset className="folder-series-picker"><legend>Cartes <span>{cards.length}/200</span></legend><div className="native-card-editor">{cards.map((card, index) => <div className="native-card-row" key={index}><strong>Carte {index + 1}</strong><label>Recto<textarea required maxLength={5000} rows={2} value={card.question} onChange={(event) => updateCard(index, "question", event.target.value)} placeholder="Question ou notion" /></label><label>Verso<textarea required maxLength={5000} rows={2} value={card.answer} onChange={(event) => updateCard(index, "answer", event.target.value)} placeholder="Réponse attendue" /></label>{cards.length > 1 && <button className="button" type="button" onClick={() => setCards((current) => current.filter((_, position) => position !== index))}>Retirer la carte {index + 1}</button>}</div>)}</div><button className="button" type="button" disabled={cards.length >= 200} onClick={() => setCards((current) => [...current, { question: "", answer: "" }])}>Ajouter une carte</button></fieldset>
        <div className="folder-editor-actions"><button className="button" type="button" onClick={() => navigate("/mes-series")}>Annuler</button><button className="button button-primary" type="submit" disabled={saving || !title.trim() || !subjectId || cards.some((card) => !card.question.trim() || !card.answer.trim())}>{saving ? "Création…" : "Créer la série"}</button></div>
      </form>
    </section>
  );
}
